import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Box from '@mui/material/Box'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'

type TablePaginationProps = {
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  pageSizeOptions?: number[]
  itemLabel?: string
}

let DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

let buildPageList = (total: number, current: number): (number | '…')[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  let lo = Math.max(2, current - 1)
  let hi = Math.min(total - 1, current + 1)
  let middle = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i) as (
    number | '…'
  )[]
  return [
    1,
    ...(lo > 2 ? (['…'] as const) : []),
    ...middle,
    ...(hi < total - 1 ? (['…'] as const) : []),
    total,
  ] as (number | '…')[]
}

let pageBtnSx = (active: boolean, disabled: boolean) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  px: '8px',
  borderRadius: '4px',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: disabled ? 'default' : 'pointer',
  border: active ? '1px solid #6694e9' : '1px solid rgba(255,255,255,.12)',
  background: active
    ? 'color-mix(in srgb, #6694e9 18%, transparent)'
    : 'transparent',
  color: active
    ? '#6694e9'
    : disabled
      ? 'rgba(255,255,255,.3)'
      : 'rgba(255,255,255,.85)',
})

let TablePagination = ({
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  itemLabel = 'items',
}: TablePaginationProps) => {
  let totalPages = Math.max(1, Math.ceil(total / pageSize))
  let currentPage = Math.min(page, totalPages)
  let start = (currentPage - 1) * pageSize
  let rangeLabel = total
    ? `Showing ${start + 1}–${Math.min(start + pageSize, total)} of ${total} ${itemLabel}`
    : `No ${itemLabel}`
  let pageList = buildPageList(totalPages, currentPage)

  return (
    <Box className="flex items-center justify-between flex-wrap gap-3 pt-4">
      <Box className="flex items-center gap-4">
        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,.6)' }}>
          {rangeLabel}
        </Typography>
        <Select
          size="small"
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          sx={{ width: 130 }}
          slotProps={{ input: { 'aria-label': `${itemLabel} per page` } }}
        >
          {pageSizeOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt} / page
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Box className="flex items-center gap-2">
        <Box
          component="button"
          type="button"
          aria-label="Previous page"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          sx={pageBtnSx(false, currentPage === 1)}
        >
          <ChevronLeftIcon sx={{ fontSize: 20 }} />
        </Box>
        {pageList.map((p, i) =>
          p === '…' ? (
            <Box
              key={`ellipsis-${i}`}
              component="span"
              sx={{ ...pageBtnSx(false, true), border: 'none' }}
            >
              …
            </Box>
          ) : (
            <Box
              key={p}
              component="button"
              type="button"
              aria-current={p === currentPage ? 'page' : undefined}
              onClick={() => onPageChange(p as number)}
              sx={pageBtnSx(p === currentPage, false)}
            >
              {p}
            </Box>
          ),
        )}
        <Box
          component="button"
          type="button"
          aria-label="Next page"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          sx={pageBtnSx(false, currentPage === totalPages)}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </Box>
      </Box>
    </Box>
  )
}

export default TablePagination
